import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { FiShare2 } from 'react-icons/fi';
import { upcomingEvents, pastEvents } from '../config/events';

const publicUrl = process.env.PUBLIC_URL || '';
pdfjs.GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.js`;

function getEventShareUrl(eventId) {
  const eventPath = `/events#${encodeURIComponent(eventId)}`;

  if (typeof window === 'undefined') return eventPath;

  return new URL(eventPath, window.location.origin).href;
}

async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the older copy path below.
    }
  }

  if (typeof document === 'undefined') return false;

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';

  document.body.appendChild(textArea);
  textArea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function isLikelyMobileShareDevice() {
  if (typeof navigator === 'undefined') return false;

  if (navigator.userAgentData?.mobile) return true;

  const userAgent = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
}

function canUseNativeMobileShare(shareData) {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (!isLikelyMobileShareDevice()) return false;

  if (typeof navigator.canShare === 'function') {
    return navigator.canShare(shareData);
  }

  return true;
}

function EventShareLink({ event }) {
  const [copyStatus, setCopyStatus] = useState('idle');
  const eventPath = `/events#${encodeURIComponent(event.id)}`;
  const label =
    copyStatus === 'copied' ? 'Copied URL' : copyStatus === 'error' ? 'Copy Failed' : 'Share';

  const handleClick = (clickEvent) => {
    if (clickEvent.metaKey || clickEvent.ctrlKey || clickEvent.shiftKey || clickEvent.altKey) return;

    clickEvent.preventDefault();

    const shareUrl = getEventShareUrl(event.id);
    const shareData = {
      title: event.title,
      text: event.title,
      url: shareUrl,
    };

    if (canUseNativeMobileShare(shareData)) {
      navigator.share(shareData).catch((error) => {
        if (error?.name !== 'AbortError') {
          copyTextToClipboard(shareUrl).then((copied) => {
            setCopyStatus(copied ? 'copied' : 'error');
          });
        }
      });
      return;
    }

    copyTextToClipboard(shareUrl).then((copied) => {
      setCopyStatus(copied ? 'copied' : 'error');
    });
  };

  useEffect(() => {
    if (copyStatus === 'idle') return undefined;

    const timeout = window.setTimeout(() => {
      setCopyStatus('idle');
    }, 2200);

    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  return (
    <Link
      to={eventPath}
      onClick={handleClick}
      className="inline-flex w-fit items-center justify-center gap-2 border border-[#c9bfae] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5b695c] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] hover:text-[#10351f] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7]"
      aria-label={`Share link for ${event.title}`}
      title={getEventShareUrl(event.id)}
    >
      <FiShare2 className="h-4 w-4" aria-hidden="true" />
      <span aria-live="polite">{label}</span>
    </Link>
  );
}

/* ─── Shared sub-components ──────────────────────────────────────────────────── */

function DateBlock({ month, day }) {
  const daySize = day.length > 2 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl';
  return (
    <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center border border-[#d5cfc0] bg-[#fffdf7] text-center sm:h-[4.5rem] sm:w-[4.5rem]">
      <span className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-[#a43b29]">
        {month}
      </span>
      <span className={`mt-1 font-serif leading-none text-[#1d2f24] ${daySize}`}>
        {day}
      </span>
    </div>
  );
}

function EventFlyer({ event }) {
  if (!event.pdfUrl) {
    return null;
  }

  const pdfLabel = event.pdfLabel || `${event.title} PDF`;

  return (
    <div className="mt-6">
      <figure className="event-flyer-preview w-full max-w-xs overflow-hidden border border-[#d5cfc0] bg-white shadow-sm sm:max-w-sm">
        <Document
          file={event.pdfUrl}
          loading={
            <div className="flex aspect-[3/4] items-center justify-center bg-[#f8f4ea] px-4 text-sm text-[#70806f]">
              Loading flyer...
            </div>
          }
          error={
            <div className="bg-[#f8f4ea] px-4 py-5 text-sm leading-6 text-[#70806f]">
              The flyer preview could not load. Please use the download link below.
            </div>
          }
        >
          <Page
            pageNumber={1}
            width={360}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
        <figcaption className="border-t border-[#d5cfc0] bg-[#fffdf7] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#70806f]">
          Event flyer
        </figcaption>
      </figure>

      <div className="mt-3 flex flex-wrap gap-3">
        <a
          href={event.pdfUrl}
          download
          aria-label={`Download ${pdfLabel}`}
          className="inline-flex items-center justify-between border border-[#43513f] bg-[#10351f] px-4 py-3 text-sm font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7] sm:justify-center sm:gap-3"
        >
          <span>Download PDF</span>
          <span aria-hidden="true">&darr;</span>
        </a>
      </div>
    </div>
  );
}

/* ─── Upcoming event card ─────────────────────────────────────────────────────── */

function UpcomingEventCard({ event }) {
  return (
    <article
      id={event.id}
      className="group grid scroll-mt-24 gap-5 border border-[#d5cfc0] bg-[#fffdf7] p-5 transition-shadow hover:shadow-md sm:grid-cols-[auto_1fr] sm:gap-6 sm:p-7"
    >
      <DateBlock month={event.month} day={event.day} />

      <div className="min-w-0">
        <EventShareLink event={event} />
        <div className="flex items-start justify-between gap-3">
          <h3 className="mt-3 font-serif text-2xl leading-tight text-[#18271e] sm:text-3xl">
            {event.title}
          </h3>
          <span
            className="mt-1 text-[#a43b29] text-xl leading-none transition-transform group-hover:translate-x-1"
            aria-hidden="true"
          >
            &rarr;
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#70806f]">
          {event.time && event.time !== 'TBA' && <span>{event.time}</span>}
          {event.time === 'TBA' && <span>Time TBA</span>}
          <span>{event.location}</span>
          {event.rainDate && <span>{event.rainDate}</span>}
          <span>{event.year}</span>
        </div>

        {event.description && (
          <p className="mt-4 max-w-2xl text-base leading-7 text-[#445044]">
            {event.description}
          </p>
        )}

        <EventFlyer event={event} />
      </div>
    </article>
  );
}

/* ─── Past event card ──────────────────────────────────────────────────────────── */

function PastEventCard({ event }) {
  return (
    <article
      id={event.id}
      className="grid scroll-mt-24 gap-5 border border-[#d5cfc0] bg-[#f8f4ea] p-5 sm:grid-cols-[auto_1fr] sm:p-7"
    >
      <DateBlock month={event.month} day={event.day} />

      <div className="min-w-0">
        <EventShareLink event={event} />
        <h3 className="mt-3 font-serif text-xl leading-tight text-[#18271e] sm:text-2xl">
          {event.title}
        </h3>

        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#70806f]">
          {event.time && <span>{event.time}</span>}
          <span>{event.location}</span>
          <span>{event.year}</span>
        </div>

        {event.description && (
          <p className="mt-3 max-w-2xl text-sm leading-7 text-[#4f5c50]">
            {event.description}
          </p>
        )}

        {event.image && (
          <img
            src={event.image}
            alt=""
            className="mt-4 h-40 w-full object-cover sm:h-48"
          />
        )}

        <EventFlyer event={event} />
      </div>
    </article>
  );
}

/* ─── Decorative botanical SVG ──────────────────────────────────────────────── */

function BotanicalAccent() {
  return (
    <div className="pointer-events-none hidden opacity-50 md:block" aria-hidden="true">
      <svg viewBox="0 0 180 132" className="h-32 w-44 text-[#314935]">
        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2">
          <path d="M28 116 C34 82 36 52 29 22" />
          <path d="M31 77 C17 67 12 55 15 40 C31 44 39 54 35 70" />
          <path d="M34 56 C47 47 53 35 51 18 C36 22 27 36 31 51" />
          <path d="M74 116 C76 80 80 50 96 18" />
          <path d="M79 76 C64 66 60 52 66 39 C80 45 86 57 82 71" />
          <path d="M91 44 C108 43 120 34 125 18 C108 16 96 24 91 39" />
          <path d="M128 116 C126 88 132 58 154 26" />
          <path d="M132 80 C148 78 162 68 168 53 C151 50 139 61 134 74" />
          <path d="M144 48 C132 38 129 26 134 12 C147 17 154 29 151 43" />
          <circle cx="26" cy="20" r="7" />
          <circle cx="96" cy="17" r="8" />
          <circle cx="155" cy="24" r="7" />
        </g>
      </svg>
    </div>
  );
}

/* ─── Empty state ────────────────────────────────────────────────────────────── */

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-[#cfc7b6] px-8 py-14 text-center">
      <p className="font-serif text-2xl text-[#8e9e8e]">Nothing here yet</p>
      <p className="mt-2 text-sm text-[#70806f]">{label}</p>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────────── */

function EventsPage() {
  const { hash } = useLocation();

  // Scroll to anchor when navigating from another page
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        // Small delay to let layout settle
        const timer = setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
        return () => clearTimeout(timer);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">
      {/* ── Page hero ─────────────────────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
          <div className="flex items-end justify-between gap-6">
            <div>
              <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl md:text-7xl">
                Events
              </h1>
              <div className="mt-5 h-px w-24 bg-[#304536]" />
              <p className="font-handwritten mt-5 max-w-xl text-xl leading-snug text-[#243c2c] sm:text-2xl">
                Come grow with us. Something's always happening at the garden.
              </p>
            </div>
            <BotanicalAccent />
          </div>
        </div>
      </section>

      {/* ── Upcoming events ───────────────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
          {/* Section heading */}
          <div className="mb-8 flex items-end justify-between gap-4 border-b border-[#cfc7b6] pb-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                What's coming up
              </p>
              <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
                Upcoming Events
              </h2>
            </div>
            <span className="hidden text-sm text-[#566556] sm:inline">2026 season</span>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="grid gap-5 md:gap-7">
              {upcomingEvents.map((event) => (
                <UpcomingEventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <EmptyState label="Check back soon for upcoming events." />
          )}
        </div>
      </section>

      {/* ── Past events ───────────────────────────────────────────── */}
      <section className="bg-[#f1eadc]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
          {/* Section heading */}
          <div className="mb-8 flex items-end justify-between gap-4 border-b border-[#cfc7b6] pb-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Look back
              </p>
              <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
                Past Events
              </h2>
            </div>
          </div>

          {pastEvents.length > 0 ? (
            <div className="grid gap-5 md:gap-7">
              {pastEvents.map((event) => (
                <PastEventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <EmptyState label="Past events will be listed here." />
          )}
        </div>
      </section>

      {/* ── Call to action ────────────────────────────────────────── */}
      <section className="border-t border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-16 lg:px-16">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-serif text-3xl text-[#10351f] sm:text-4xl">
                Want to be involved?
              </h2>
              <p className="mt-3 max-w-lg text-base leading-7 text-[#445044]">
                Whether you'd like to volunteer at an event, host a workshop, or just
                stay in the loop — we'd love to hear from you.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex items-center justify-between border border-[#43513f] px-5 py-4 text-sm font-medium text-[#18271e] transition-colors hover:bg-[#10351f] hover:text-[#fbf8ef] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:justify-center sm:gap-3"
              >
                <span>Get in Touch</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
              <Link
                to="/apply"
                className="inline-flex items-center justify-between border border-[#43513f] bg-[#10351f] px-5 py-4 text-sm font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:justify-center sm:gap-3"
              >
                <span>Join the Garden</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default EventsPage;
